import { useNavigate } from "@tanstack/react-router";
import { Facility } from "types/facility.type";
import {
  getUppdragDateAsString,
  getUppdragLabel,
  getWeekNumberString,
} from "types/uppdrag.type";
import {
  ContentContainer,
  HeaderContainer,
  ShadowCard,
} from "components/ShadowCard";
import { useAddUppdragFromFacilityPage } from "../uppdrag/add-uppdrag-modal/useAddUppdragFromFacilityPage";
import { AddUppdragFromFacilityPageModal } from "../uppdrag/add-uppdrag-modal/AddUppdragFromFacilityPageModal";
import { Headline4 } from "components/typography/Headline4";
import EmptyState from "components/EmptyState";
import { Table, TBody, TR, TD } from "components/table";
import { UppdragStatusTag } from "components/UppdragStatusTag";
import { firstNameWithInitials } from "helpers/string";
import { PriorityIcon } from "../uppdrag-list-page/PriorityIcon";
import { UppdragTypeList } from "../uppdrag-list-page/UppdragTypeList";
import PrimaryButton from "components/buttons/PrimaryButton";

type Props = {
  facility: Facility;
};

export const FacilityUppdragListTab = ({ facility }: Props) => {
  const navigate = useNavigate();

  const logic = useAddUppdragFromFacilityPage(facility);

  return (
    <ShadowCard className="max-w-5xl">
      <AddUppdragFromFacilityPageModal logic={logic} />
      <HeaderContainer>
        <Headline4>Uppdrag</Headline4>
        {facility.uppdragList.length > 0 && (
          <PrimaryButton size="small" onClick={logic.openModal}>
            + Nytt uppdrag
          </PrimaryButton>
        )}
      </HeaderContainer>

      <ContentContainer>
        {facility.uppdragList.length === 0 ? (
          <EmptyState
            onClick={logic.openModal}
            header="Inga uppdrag"
            subHeader=""
            buttonText="Nytt uppdrag"
            iconType="uppdrag"
          />
        ) : (
          <Table>
            <TBody>
              {facility.uppdragList.map((uppdrag) => (
                <TR
                  key={uppdrag.id}
                  onClick={() => {
                    navigate({
                      to: "/app/uppdrag/$uuid",
                      params: { uuid: uppdrag.uuid },
                    });
                  }}
                >
                  <TD isFirstColumn>{getUppdragLabel(uppdrag)}</TD>
                  <TD>
                    <div className="flex flex-col space-y-0.5">
                      <span className="flex items-center text-gray-950">
                        {uppdrag.isPrioritized && (
                          <PriorityIcon className="mr-0.5 h-4 w-4" />
                        )}
                        {getUppdragDateAsString(uppdrag)}
                      </span>
                      <span className="flex items-center text-gray-500">
                        {getWeekNumberString(uppdrag)}
                      </span>
                    </div>
                  </TD>
                  <TD>
                    <UppdragTypeList uppdrag={uppdrag} />
                  </TD>
                  <TD>{firstNameWithInitials(uppdrag.assignedToUser?.name)}</TD>
                  <TD>
                    <UppdragStatusTag status={uppdrag.status} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </ContentContainer>
    </ShadowCard>
  );
};
